import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/auth/useAuth';
import { api } from '@/api/client';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'block px-3 py-2 rounded text-sm transition-colors',
    isActive
      ? 'bg-brand-500 text-white'
      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
  );

export function Layout() {
  const { session, signOut, hasPermission } = useAuth();
  const [highCount, setHighCount] = useState<number>(0);

  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      try {
        const res = await api.get<{ counts?: { high?: number } }>(
          '/back-office/action-needed'
        );
        if (alive) setHighCount(res.data?.counts?.high ?? 0);
      } catch {
        /* silent — sidebar badge is non-critical */
      }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const canRefunds = hasPermission('refunds.view');
  const canViewUsers = hasPermission('users.view');
  const canManageAdmins = hasPermission('admins.manage');
  const canViewVtu = hasPermission('vtu.view');
  const canManageDeletions = hasPermission('account_deletion.manage');
  const canViewArchive = hasPermission('archive.view');
  const canViewFees = hasPermission('fees.view');

  return (
    <div className="flex h-full">
      <aside className="flex w-60 flex-col bg-slate-900 text-white">
        <div className="px-5 py-6 border-b border-slate-800">
          <div className="text-lg font-semibold">SwiftMedix</div>
          <div className="text-xs text-slate-400">Back Office</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavLink to="/dashboard" className={navLinkClass}>
            Dashboard
          </NavLink>
          <NavLink to="/users" className={navLinkClass}>
            Users
          </NavLink>
          {canViewUsers && (
            <NavLink to="/broadcast-segments" className={navLinkClass}>
              Broadcast Segments
            </NavLink>
          )}
          <NavLink to="/products" className={navLinkClass}>
            Products
          </NavLink>
          <NavLink to="/orders" className={navLinkClass}>
            Orders
          </NavLink>
          <NavLink to="/consultations" className={navLinkClass}>
            Consultations
          </NavLink>
          <NavLink to="/settlements" className={navLinkClass}>
            Settlements
          </NavLink>
          <NavLink to="/action-needed" className={navLinkClass}>
            <span className="flex items-center justify-between">
              <span>Action Needed</span>
              {highCount > 0 && (
                <span className="ml-2 rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {highCount}
                </span>
              )}
            </span>
          </NavLink>
          {canRefunds && (
            <NavLink to="/refunds" className={navLinkClass}>
              Refunds
            </NavLink>
          )}
          {canViewVtu && (
            <NavLink to="/vtu" className={navLinkClass}>
              Airtime &amp; Data
            </NavLink>
          )}
          {canViewFees && (
            <NavLink to="/fees" className={navLinkClass}>
              Platform Fees
            </NavLink>
          )}
          {canManageDeletions && (
            <NavLink to="/account-deletions" className={navLinkClass}>
              Account Deletions
            </NavLink>
          )}
          {canViewArchive && (
            <NavLink to="/account-deletions/archive" className={navLinkClass}>
              Deletion Archive
            </NavLink>
          )}
          <NavLink to="/terms" className={navLinkClass}>
            Terms & Conditions
          </NavLink>
          {canManageAdmins && (
            <NavLink to="/team" className={navLinkClass}>
              Team &amp; Roles
            </NavLink>
          )}
        </nav>
        <div className="px-5 py-4 border-t border-slate-800 text-xs">
          <div className="truncate text-slate-300">{session?.email}</div>
          <div className="text-slate-500">{session?.role.role_type}</div>
          <button
            type="button"
            onClick={signOut}
            className="mt-2 text-brand-100 hover:text-white"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
